import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box } from '@mui/material';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import {
  documentsApi,
  formBundleSignatureRequestApi,
  formDeliveryRequestApi,
  formsSignatureRequestApi,
} from 'app/services/api';
import DocumentView from 'app/shared/components/document/DocumentView';
import InlineAlertBanner from 'app/shared/componentsRestyled/common/InlineAlertBanner';
import {
  documentViewWithBannerWrapperStyles,
  documentViewWrapperStyles,
} from 'app/shared/styles/documentViewDialog.styles';
import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_DATETIME_FORMAT_FOR_FILE_NAME } from 'constants/date';
import { FeatureFlag } from 'constants/featureFlags';
import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { StatusEnum } from 'constants/status';
import { printFileFromBlob } from 'utils/file';
import { logger } from 'utils/logger';
import { downloadOrShareBlob } from 'utils/nativeFile';

import { openDialogAction, showMessageAction } from 'store/global/actions';
import { selectFeatureFlagByKey } from 'store/global/selectors';

import { DisclosureDeliveryTestIds } from '../../DisclosureDelivery.testIds';

import {
  DOCUMENT_VIEW_ID,
  DOWNLOAD_ERROR_MESSAGE,
  PREVIEW_ERROR_MESSAGE,
  PREVIEW_ERROR_MESSAGE_BY_STATUS,
  PRINT_ERROR_MESSAGE,
  VIEW_DOCUMENTS_HEADER,
} from './constants';

/**
 * View / download / print a sent disclosure package (a form delivery request) as one PDF, or,
 * with `documentId`, one recipient's signed copy of it.
 * The file is named after the package and the date it was generated.
 */
const useSignatureRequestDocumentViewer = ({
  loanId,
  signatureId,
  fileName,
  fileDate,
  documentId = null,
  header = VIEW_DOCUMENTS_HEADER,
  isDownloadable = true,
  isPrintable = true,
  previewRequest = null,
  notice = null,
  onConflict = _.noop,
  setIsLoading = _.noop,
}) => {
  const dispatch = useDispatch();

  const isFormsConsolidationEnabled = useSelector(
    selectFeatureFlagByKey(FeatureFlag.FormsConsolidationEnabled),
  );

  const signatureRequestApi = isFormsConsolidationEnabled
    ? formsSignatureRequestApi
    : formBundleSignatureRequestApi;

  // DocumentView passes its sentinel docId here; the real id stays in this closure so the copy is
  // never fetched into the documents store (and so never shows up in the Documents tree).
  const getDocumentCall = useCallback(() => {
    if (documentId) {
      return documentsApi.getContent({ documentId });
    }

    if (previewRequest) {
      return formDeliveryRequestApi.getRecipientSignerView(previewRequest).catch(error => {
        const status = error?.response?.status;

        dispatch(
          showMessageAction({
            message: PREVIEW_ERROR_MESSAGE_BY_STATUS[status] ?? PREVIEW_ERROR_MESSAGE,
            variant: 'error',
          }),
        );

        if (status === HTTP_RESPONSE_STATUS_CODES.CONFLICT) {
          onConflict();
        }

        throw error;
      });
    }

    return signatureRequestApi.getPdfDocumentBundle({ loanId, signatureId });
  }, [documentId, previewRequest, signatureRequestApi, loanId, signatureId, dispatch, onConflict]);

  const onDownloadDocument = useCallback(() => {
    if (!isDownloadable) {
      return;
    }

    setIsLoading(true);

    getDocumentCall()
      .then(({ data }) =>
        downloadOrShareBlob(
          data,
          `${fileName} ${(fileDate ? dayjs(fileDate) : dayjs()).format(COMMON_DATETIME_FORMAT_FOR_FILE_NAME)}`,
        ),
      )
      .catch(error => {
        dispatch(showMessageAction({ message: DOWNLOAD_ERROR_MESSAGE, variant: 'error' }));
        logger.error(error);
      })
      .finally(() => setIsLoading(false));
  }, [isDownloadable, setIsLoading, getDocumentCall, fileName, fileDate, dispatch]);

  const onPrintDocument = useCallback(() => {
    if (!isPrintable) {
      return;
    }

    getDocumentCall()
      .then(({ data }) => printFileFromBlob(data))
      .catch(error => {
        dispatch(showMessageAction({ message: PRINT_ERROR_MESSAGE, variant: 'error' }));
        logger.error(error);
      });
  }, [isPrintable, dispatch, getDocumentCall]);

  const onViewDocuments = useCallback(() => {
    dispatch(
      openDialogAction({
        header,
        size: ModalSize.XL,
        hasScrollY: false,
        body: () => (
          <Box sx={notice ? documentViewWithBannerWrapperStyles : documentViewWrapperStyles}>
            {notice && (
              <InlineAlertBanner
                dataTestId={DisclosureDeliveryTestIds.PREVIEW_NOTICE}
                message={notice}
                variant={StatusEnum.WARNING}
              />
            )}
            <DocumentView
              isView
              docId={DOCUMENT_VIEW_ID}
              getDocumentCall={getDocumentCall}
              isControlVisible={false}
              isDownloadable={isDownloadable}
              isPrintable={isPrintable}
              supressErrorMessage={!!previewRequest}
              onDownloadDocument={onDownloadDocument}
              onPrintDocument={onPrintDocument}
            />
          </Box>
        ),
      }),
    );
  }, [
    dispatch,
    header,
    notice,
    previewRequest,
    getDocumentCall,
    isDownloadable,
    isPrintable,
    onDownloadDocument,
    onPrintDocument,
  ]);

  return { onViewDocuments };
};

export default useSignatureRequestDocumentViewer;
